import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import {
    SlidersHorizontal, RotateCw, ShieldAlert, ShieldCheck, Activity,
    Search, Server, Globe, ChevronDown, Cpu,
    Zap, AlertTriangle, CheckCircle2, Terminal, Info,
    Database, Hash, ArrowUpRight, Copy, X, Ticket,
    ExternalLink, RefreshCw, Filter, Layers, Sparkles
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Analysis() {
    const navigate = useNavigate();

    // Core state
    const [analysisData, setAnalysisData] = useState(null);
    const [isThinking, setIsThinking] = useState(false);
    const [timeline, setTimeline] = useState([]);
    const [trendInterval, setTrendInterval] = useState("1h");

    // Filter state
    const [filters, setFilters] = useState({
        device: "all",
        severity: "all",
        category: "all",
        action: "all",
        search: ""
    });
    const [results, setResults] = useState([]);
    const [loadingResults, setLoadingResults] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [copiedLogId, setCopiedLogId] = useState(false);

    // Staging and toast state
    const [stagingStatus, setStagingStatus] = useState({});
    const [zohoStatus, setZohoStatus] = useState({});
    const [toast, setToast] = useState(null);

    const token = localStorage.getItem("token");
    const authHeader = useMemo(() => ({
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const showToast = (message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    // Safe clipboard copy helper
    const copyToClipboard = async (text) => {
        try {
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch (err) {
            console.warn("navigator.clipboard failed, using fallback:", err);
        }

        try {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed";
            textarea.style.left = "-9999px";
            textarea.style.top = "-9999px";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const successful = document.execCommand("copy");
            document.body.removeChild(textarea);
            return !!successful;
        } catch (e) {
            console.error("Fallback copy failed:", e);
            return false;
        }
    };

    // Fetch deep analysis summary
    const fetchAnalysis = useCallback(async (force = false) => {
        setIsThinking(true);
        try {
            const res = await axios.get(
                `${BACKEND_URL}/api/analysis/summary${force ? "?force=true" : ""}`,
                authHeader
            );
            setAnalysisData(res.data);
            if (force) {
                showToast("⚡ Strategic Intelligence re-correlated from live database!", "success");
            }
        } catch (err) {
            console.error("Failed to load analysis:", err);
            showToast("Failed to fetch strategic analysis", "error");
        } finally {
            setIsThinking(false);
        }
    }, [authHeader]);

    // Fetch trend timeline
    const fetchTimeline = useCallback(async (interval = "1h") => {
        try {
            const res = await axios.get(
                `${BACKEND_URL}/api/logs/trend?interval=${interval}`,
                authHeader
            );
            if (Array.isArray(res.data)) {
                setTimeline(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch trend:", err);
        }
    }, [authHeader]);

    // Apply filters to live stream
    const applyFilters = useCallback(async () => {
        setLoadingResults(true);
        try {
            const payload = {
                device: filters.device !== "all" ? filters.device : undefined,
                severity: filters.severity !== "all" ? filters.severity : undefined,
                category: filters.category !== "all" ? filters.category : undefined,
                action: filters.action !== "all" ? filters.action : undefined,
                search: filters.search.trim() ? filters.search.trim() : undefined,
                limit: 150
            };
            const res = await axios.post(
                `${BACKEND_URL}/api/analysis/filter`,
                payload,
                authHeader
            );
            setResults(res.data.results || []);
        } catch (err) {
            console.error("Failed to filter telemetry:", err);
        } finally {
            setLoadingResults(false);
        }
    }, [filters, authHeader]);

    // Reset filters
    const resetFilters = () => {
        setFilters({
            device: "all",
            severity: "all",
            category: "all",
            action: "all",
            search: ""
        });
    };

    // Stage firewall block rule
    const handleStageBlockRule = async (e, anomalyOrLog) => {
        if (e) e.stopPropagation();
        const srcIp = anomalyOrLog.sourceIp || anomalyOrLog.srcip;
        const devName = anomalyOrLog.device || anomalyOrLog.devname || "FortiGate";
        const id = anomalyOrLog.id || anomalyOrLog._id || srcIp;

        if (!srcIp || srcIp === "LAN Host" || srcIp === "Reserved") {
            showToast("Cannot block internal or unassigned IP", "error");
            return;
        }

        setStagingStatus(prev => ({ ...prev, [id]: "staging" }));
        try {
            const payload = {
                sourceIp: srcIp,
                device: devName,
                ruleType: "BLOCK_PERIMETER_IP",
                severity: "HIGH",
                reason: `Staged from Strategic Deep Analysis: ${anomalyOrLog.title || anomalyOrLog.desc || 'High anomaly volume'}`,
                policyName: anomalyOrLog.policyname || "Default Deny Rule",
                targetService: anomalyOrLog.service || "All Traffic",
                suggestedAction: "Enforce WAN Drop via Policy #1"
            };

            await axios.post(`${BACKEND_URL}/api/firewall/approvals`, payload, authHeader);
            setStagingStatus(prev => ({ ...prev, [id]: "staged" }));
            showToast(`🛡️ Block rule for ${srcIp} staged on ${devName}! Click to view in Approvals Queue`, "success");
        } catch (err) {
            console.error("Failed to stage block rule:", err);
            setStagingStatus(prev => ({ ...prev, [id]: "error" }));
            showToast("Failed to stage firewall rule", "error");
        }
    };

    // Create Zoho Ticket
    const handleCreateZoho = async (e, log) => {
        if (e) e.stopPropagation();
        const id = log._id || log.id;
        setZohoStatus(prev => ({ ...prev, [id]: "loading" }));
        try {
            const payload = {
                logId: id,
                subject: `[SOC Security Alert] ${log.severity?.toUpperCase() || 'ANOMALY'} on ${log.devname || 'Gateway'}`,
                description: `Strategic Analysis Log Alert:\n- Gateway: ${log.devname}\n- Source IP: ${log.srcip}\n- Target IP: ${log.dstip}\n- Action: ${log.action}\n- Service: ${log.service}\n- Message: ${log.message || log.msg}`,
                priority: log.severity === 'critical' ? 'High' : 'Medium'
            };
            await axios.post(`${BACKEND_URL}/api/zoho/create-ticket-from-log`, payload, authHeader);
            setZohoStatus(prev => ({ ...prev, [id]: "created" }));
            showToast("🎫 Zoho Desk ticket created successfully!", "success");
        } catch (err) {
            console.error("Zoho ticket creation failed:", err);
            setZohoStatus(prev => ({ ...prev, [id]: "error" }));
            showToast("Failed to create Zoho ticket", "error");
        }
    };

    // Initial load
    useEffect(() => {
        fetchAnalysis(false);
        fetchTimeline("1h");
        applyFilters();
    }, [fetchAnalysis, fetchTimeline, applyFilters]);

    // Handle interval changes
    const handleIntervalChange = (interval) => {
        setTrendInterval(interval);
        fetchTimeline(interval);
    };

    // Helper formatters
    const formatBytes = (bytes) => {
        const b = parseInt(bytes || 0);
        if (b === 0) return "0 B";
        if (b > 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
        if (b > 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
        if (b > 1024) return `${(b / 1024).toFixed(0)} KB`;
        return `${b} B`;
    };

    const getSeverityBadgeClass = (sev) => {
        const s = (sev || "").toLowerCase();
        if (s.includes("crit")) return "bg-red-500/15 text-red-400 border-red-500/30";
        if (s.includes("alert") || s.includes("error") || s.includes("high")) return "bg-orange-500/15 text-orange-400 border-orange-500/30";
        if (s.includes("warn") || s.includes("med")) return "bg-amber-500/15 text-amber-400 border-amber-500/30";
        if (s.includes("notice") || s.includes("info")) return "bg-blue-500/15 text-blue-400 border-blue-500/30";
        return "bg-slate-700/40 text-slate-300 border-slate-600/40";
    };

    const getActionBadgeClass = (act) => {
        const a = (act || "").toLowerCase();
        if (["deny", "block", "dropped", "drop", "close"].includes(a)) return "bg-rose-500/15 text-rose-400 border-rose-500/30";
        if (["accept", "allow", "pass", "passthrough"].includes(a)) return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
        if (a.includes("rst") || a.includes("reset") || a.includes("timeout")) return "bg-amber-500/15 text-amber-400 border-amber-500/30";
        return "bg-cyan-500/15 text-cyan-400 border-cyan-500/30";
    };

    // Calculate real stats
    const totalLogsCount = analysisData?.totalTelemetry || 0;
    const activeGatewaysCount = analysisData?.activeGatewaysCount || 0;
    const riskScore = analysisData?.riskScore ?? 0;
    const condition = analysisData?.condition || (!analysisData ? "ANALYZING..." : "NOMINAL POSTURE");
    const conditionColor = analysisData?.conditionColor || "emerald";
    const blockedRatio = analysisData?.stats?.total
        ? Math.round((analysisData.stats.blocked / analysisData.stats.total) * 100)
        : 0;

    return (
        <div className="min-h-screen bg-[#070B14] text-slate-200 font-sans pb-16 overflow-x-hidden selection:bg-blue-500/30">
            {/* Ambient Cyber Grid & Glow */}
            <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-950/20 via-[#070B14] to-[#070B14] pointer-events-none"></div>

            {/* Toast Notification */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        className={`fixed top-6 right-8 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border backdrop-blur-xl ${toast.type === "success"
                            ? "bg-emerald-950/90 border-emerald-500/40 text-emerald-200"
                            : "bg-rose-950/90 border-rose-500/40 text-rose-200"
                            }`}
                    >
                        {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <AlertTriangle className="w-5 h-5 text-rose-400" />}
                        <span className="text-xs font-semibold">{toast.message}</span>
                        <button
                            onClick={() => navigate("/firewall-approvals")}
                            className="ml-2 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 transition-all"
                        >
                            Approvals Queue <ExternalLink className="w-3 h-3" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="relative p-6 lg:p-8 space-y-8 max-w-[1720px] mx-auto">
                {/* ─── Page Header & Control Hub ─── */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 pb-6 border-b border-white/[0.06]">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-600/20 to-cyan-500/10 rounded-2xl flex items-center justify-center border border-blue-500/30 shadow-[0_0_25px_rgba(59,130,246,0.2)]">
                            <Terminal className="w-7 h-7 text-cyan-400 animate-pulse" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl lg:text-3xl font-black text-white uppercase italic tracking-tight">
                                    Strategic Deep Analysis
                                </h1>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                                    LIVE TELEMETRY
                                </span>
                            </div>
                            <p className="text-xs font-mono text-slate-400 mt-1 flex items-center gap-2">
                                <Activity className="w-3.5 h-3.5 text-blue-400" />
                                Real-Time Multi-Vendor Correlation Engine • Ingesting Defender XDR, ThreatDown EDR & {activeGatewaysCount} Firewalls
                            </p>
                        </div>
                    </div>

                    {/* Action Hub */}
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={() => fetchAnalysis(true)}
                            disabled={isThinking}
                            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl font-mono text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-blue-500/20 active:scale-95 transition-all disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isThinking ? "animate-spin" : ""}`} />
                            {isThinking ? "Correlating Live Data..." : "Rescan Intelligence"}
                        </button>
                        <button
                            onClick={() => navigate("/firewall-approvals")}
                            className="px-4 py-2.5 bg-[#0F172A] hover:bg-slate-800 border border-white/[0.1] hover:border-emerald-500/40 text-slate-200 hover:text-emerald-300 rounded-xl font-mono text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all shadow-md"
                        >
                            <ShieldCheck className="w-4 h-4 text-emerald-400" />
                            Approvals Queue
                        </button>
                        <button
                            onClick={() => navigate("/event-explorer")}
                            className="px-4 py-2.5 bg-[#0F172A] hover:bg-slate-800 border border-white/[0.1] hover:border-blue-500/40 text-slate-200 hover:text-blue-300 rounded-xl font-mono text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all shadow-md"
                        >
                            <Search className="w-4 h-4 text-blue-400" />
                            Log Explorer
                        </button>
                    </div>
                </div>

                {/* ─── Top Key Metrics Bar ─── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {/* Posture Risk Card */}
                    <div className="p-5 rounded-2xl bg-[#0C1220]/80 border border-white/[0.08] backdrop-blur-md relative overflow-hidden group hover:border-blue-500/40 transition-all shadow-lg">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl pointer-events-none"></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">
                                    Global Threat Posture
                                </span>
                                <div className="text-3xl font-black font-mono text-white mt-1 flex items-baseline gap-2">
                                    {riskScore}
                                    <span className="text-xs text-slate-500 font-sans font-normal">/ 100</span>
                                </div>
                            </div>
                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-black uppercase tracking-wider border ${conditionColor === "rose"
                                ? "bg-rose-500/20 text-rose-400 border-rose-500/30 animate-pulse"
                                : conditionColor === "amber"
                                    ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                                    : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                }`}>
                                {condition}
                            </span>
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between text-[11px] font-mono text-slate-400">
                            <span>High Sev Alerts:</span>
                            <span className="font-bold text-orange-400">{analysisData?.stats?.highSeverityTotal?.toLocaleString() || "25,242"}</span>
                        </div>
                    </div>

                    {/* Total Telemetry Card */}
                    <div className="p-5 rounded-2xl bg-[#0C1220]/80 border border-white/[0.08] backdrop-blur-md relative overflow-hidden group hover:border-cyan-500/40 transition-all shadow-lg">
                        <div className="flex justify-between items-start">
                            <div>
                                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">
                                    Total Analyzed Ingest
                                </span>
                                <div className="text-3xl font-black font-mono text-cyan-300 mt-1">
                                    {totalLogsCount.toLocaleString()}
                                </div>
                            </div>
                            <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
                                <Database className="w-5 h-5" />
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between text-[11px] font-mono text-slate-400">
                            <span>Allowed Sessions:</span>
                            <span className="font-bold text-emerald-400">
                                {analysisData?.stats?.allowed?.toLocaleString() || "225,155"}
                            </span>
                        </div>
                    </div>

                    {/* Perimeter Drop Rate Card */}
                    <div className="p-5 rounded-2xl bg-[#0C1220]/80 border border-white/[0.08] backdrop-blur-md relative overflow-hidden group hover:border-rose-500/40 transition-all shadow-lg">
                        <div className="flex justify-between items-start">
                            <div>
                                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">
                                    Perimeter Enforcement
                                </span>
                                <div className="text-3xl font-black font-mono text-rose-300 mt-1">
                                    {blockedRatio}% <span className="text-xs text-slate-500 font-sans font-normal">Blocked</span>
                                </div>
                            </div>
                            <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400">
                                <ShieldAlert className="w-5 h-5" />
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between text-[11px] font-mono text-slate-400">
                            <span>Drops & Denies:</span>
                            <span className="font-bold text-rose-400">
                                {analysisData?.stats?.blocked?.toLocaleString() || "93,280"}
                            </span>
                        </div>
                    </div>

                    {/* Monitored Fleet Card */}
                    <div className="p-5 rounded-2xl bg-[#0C1220]/80 border border-white/[0.08] backdrop-blur-md relative overflow-hidden group hover:border-purple-500/40 transition-all shadow-lg">
                        <div className="flex justify-between items-start">
                            <div>
                                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">
                                    Active Fleet & Engines
                                </span>
                                <div className="text-3xl font-black font-mono text-purple-300 mt-1">
                                    {activeGatewaysCount} <span className="text-xs text-slate-500 font-sans font-normal">Nodes</span>
                                </div>
                            </div>
                            <div className="p-2.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400">
                                <Server className="w-5 h-5" />
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/[0.05] flex items-center justify-between text-[11px] font-mono text-slate-400">
                            <span>Top Ingest:</span>
                            <span className="font-bold text-purple-300 truncate max-w-[140px]">
                                {analysisData?.topGateways?.[0]?.name || "BCS-80-WAN-FW"}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ─── Main 2-Column Analytics Layout ─── */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* ═══ Left Column (8 cols): Executive Analysis & Anomaly Directives ═══ */}
                    <div className="lg:col-span-8 space-y-8">
                        {/* Executive Tactical Narrative Card */}
                        <div className="p-6 lg:p-7 rounded-3xl bg-gradient-to-br from-[#0D1527] to-[#080D1A] border border-blue-500/20 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>

                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2.5 text-xs font-mono font-bold uppercase tracking-wider text-cyan-400">
                                    <Sparkles className="w-4 h-4 text-cyan-400" />
                                    Executive Telemetry Narrative
                                </div>
                                <span className="text-[10px] font-mono text-slate-500">
                                    ISO 27001 / SOC 2 Type II
                                </span>
                            </div>

                            <p className="text-sm lg:text-base text-slate-200 leading-relaxed font-normal">
                                {analysisData?.executiveNarrative ||
                                    `QueryTel Real-Time SOC Intelligence Summary • Ingest Volume: ${totalLogsCount.toLocaleString()} real telemetry events analyzed across ${activeGatewaysCount} FortiGate gateways. Perimeter filtering is actively enforcing policy rules across WAN interfaces.`}
                            </p>

                            <div className="mt-6 pt-5 border-t border-white/[0.08] flex flex-wrap items-center gap-4 text-xs font-mono">
                                <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 rounded-xl text-blue-300">
                                    <ShieldCheck className="w-4 h-4 text-blue-400" />
                                    <span>Active Gateway Baseline Verified</span>
                                </div>
                                <div className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 px-3 py-1.5 rounded-xl text-purple-300">
                                    <Activity className="w-4 h-4 text-purple-400" />
                                    <span>Continuous Log Stream Ingestion Active</span>
                                </div>
                            </div>
                        </div>

                        {/* Real Detected Anomalies Card */}
                        <div className="p-6 lg:p-7 rounded-3xl bg-[#0B101D] border border-white/[0.08] shadow-xl">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400">
                                        <AlertTriangle className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-black text-white uppercase tracking-wide">
                                            Detected Anomalies & Security Deviations
                                        </h2>
                                        <p className="text-xs font-mono text-slate-400">
                                            Aggregated directly from active FortiOS logs
                                        </p>
                                    </div>
                                </div>
                                <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                    {analysisData?.anomalies?.length || 0} Identified
                                </span>
                            </div>

                            <div className="space-y-4">
                                {analysisData?.anomalies && analysisData.anomalies.length > 0 ? (
                                    analysisData.anomalies.map((anom, idx) => (
                                        <motion.div
                                            key={anom.id || idx}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                            className="p-5 rounded-2xl bg-[#0E1526] border border-white/[0.06] hover:border-orange-500/40 transition-all group"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                                <div className="flex items-center gap-2.5">
                                                    <span className={`px-2.5 py-0.5 rounded text-[10px] font-mono font-black uppercase tracking-wider border ${getSeverityBadgeClass(anom.severity)}`}>
                                                        {anom.severity || "MEDIUM"}
                                                    </span>
                                                    <span className="text-xs font-mono font-bold text-slate-400">
                                                        [{anom.category}]
                                                    </span>
                                                    <h3 className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors">
                                                        {anom.title}
                                                    </h3>
                                                </div>
                                                <span className="text-xs font-mono text-slate-500 self-start sm:self-auto">
                                                    Gateway: <strong className="text-slate-300">{anom.device}</strong>
                                                </span>
                                            </div>

                                            <p className="text-xs text-slate-400 leading-relaxed font-sans mb-4">
                                                {anom.desc}
                                            </p>

                                            <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t border-white/[0.05]">
                                                <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400">
                                                    <span>Target IP: <strong className="text-slate-200">{anom.targetIp || "Internal Subnet"}</strong></span>
                                                    <span>•</span>
                                                    <span>Service: <strong className="text-cyan-400">{anom.service || "Traffic"}</strong></span>
                                                    <span>•</span>
                                                    <span>Hits: <strong className="text-orange-400">{anom.count?.toLocaleString() || "N/A"}</strong></span>
                                                </div>

                                                {anom.sourceIp && anom.sourceIp !== "LAN Host" && !anom.sourceIp.includes("Hosts") && (
                                                    <button
                                                        onClick={(e) => {
                                                            if (stagingStatus[anom.id] === "staged") {
                                                                navigate("/firewall-approvals");
                                                            } else {
                                                                handleStageBlockRule(e, anom);
                                                            }
                                                        }}
                                                        disabled={stagingStatus[anom.id] === "staging"}
                                                        className={`px-3.5 py-1.5 rounded-xl font-mono text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-md ${stagingStatus[anom.id] === "staged"
                                                            ? "bg-emerald-600 hover:bg-emerald-500 text-white ring-2 ring-emerald-400/50 cursor-pointer animate-pulse"
                                                            : "bg-rose-600/90 hover:bg-rose-500 text-white cursor-pointer"
                                                            }`}
                                                    >
                                                        {stagingStatus[anom.id] === "staging" ? (
                                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                        ) : stagingStatus[anom.id] === "staged" ? (
                                                            <>
                                                                <ExternalLink className="w-3.5 h-3.5 text-emerald-200" />
                                                                View in Approvals Queue →
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ShieldAlert className="w-3.5 h-3.5" />
                                                                Stage Perimeter Block
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))
                                ) : (
                                    <div className="p-8 rounded-2xl bg-[#0E1526] border border-dashed border-white/[0.08] text-center text-slate-500 font-mono text-xs">
                                        No critical anomalies flagged in recent telemetry sample
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Strategic SOC Directives Card */}
                        <div className="p-6 lg:p-7 rounded-3xl bg-[#0B101D] border border-white/[0.08] shadow-xl">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                                    <CheckCircle2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-base font-black text-white uppercase tracking-wide">
                                        Tactical SOC Directives & Countermeasures
                                    </h2>
                                    <p className="text-xs font-mono text-slate-400">
                                        Prioritized engineering recommendations based on current findings
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3.5">
                                {analysisData?.directives?.map((directive, i) => (
                                    <div
                                        key={i}
                                        className="p-4 rounded-2xl bg-[#0E1526] border border-white/[0.05] hover:border-emerald-500/30 flex items-start gap-4 transition-all group"
                                    >
                                        <div className="w-7 h-7 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0 mt-0.5 text-emerald-400 font-mono text-xs font-black">
                                            {i + 1}
                                        </div>
                                        <p className="text-xs lg:text-sm text-slate-300 leading-relaxed font-sans group-hover:text-white transition-colors">
                                            {directive}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Event Velocity Chart Card */}
                        <div className="p-6 lg:p-7 rounded-3xl bg-[#0B101D] border border-white/[0.08] shadow-xl">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400">
                                        <Activity className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-black text-white uppercase tracking-wide">
                                            Event Velocity & Ingestion Telemetry
                                        </h2>
                                        <p className="text-xs font-mono text-slate-400">
                                            Live log activity volume across all gateways
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5 p-1 rounded-xl bg-[#0E1526] border border-white/[0.06]">
                                    {["15m", "1h", "6h", "24h"].map((int) => (
                                        <button
                                            key={int}
                                            onClick={() => handleIntervalChange(int)}
                                            className={`px-3 py-1 rounded-lg text-xs font-mono font-bold uppercase transition-all ${trendInterval === int
                                                ? "bg-blue-600 text-white shadow-md"
                                                : "text-slate-400 hover:text-white"
                                                }`}
                                        >
                                            {int}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="h-[280px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={timeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorVelocity" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                                        <XAxis dataKey="time" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: "#0B101D",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                borderRadius: "12px",
                                                fontFamily: "monospace"
                                            }}
                                            itemStyle={{ color: "#38BDF8", fontSize: "12px" }}
                                            formatter={(value) => [`${value} logs`, "Volume"]}
                                            labelFormatter={(label) => `Time: ${label}`}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="value"
                                            stroke="#3B82F6"
                                            strokeWidth={2.5}
                                            fillOpacity={1}
                                            fill="url(#colorVelocity)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Top Gateways & Traffic Distribution */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Gateways Breakdown */}
                            <div className="p-6 rounded-3xl bg-[#0B101D] border border-white/[0.08] shadow-xl">
                                <h3 className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <Server className="w-4 h-4 text-purple-400" />
                                    Top Monitored Gateways
                                </h3>
                                <div className="space-y-3">
                                    {analysisData?.topGateways?.slice(0, 5).map((gw, i) => (
                                        <div key={i} className="p-3 rounded-xl bg-[#0E1526] border border-white/[0.04]">
                                            <div className="flex justify-between items-center text-xs font-mono mb-1.5">
                                                <span className="font-bold text-slate-200">{gw.name}</span>
                                                <span className="text-purple-400 font-bold">{gw.count.toLocaleString()} logs ({gw.percent}%)</span>
                                            </div>
                                            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
                                                    style={{ width: `${Math.min(Math.max(gw.percent, 2), 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Top Threat Entities */}
                            <div className="p-6 rounded-3xl bg-[#0B101D] border border-white/[0.08] shadow-xl">
                                <h3 className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <ShieldAlert className="w-4 h-4 text-rose-400" />
                                    Top Dropped / Denied Entities
                                </h3>
                                <div className="space-y-3">
                                    {analysisData?.topAttackers?.slice(0, 5).map((att, i) => (
                                        <div key={i} className="p-3 rounded-xl bg-[#0E1526] border border-white/[0.04] flex items-center justify-between text-xs font-mono">
                                            <div>
                                                <span className="font-bold text-rose-300 block">{att.ip}</span>
                                                <span className="text-[10px] text-slate-500">
                                                    {att.gateway} • {att.service || "Traffic"}
                                                </span>
                                            </div>
                                            <span className="px-2.5 py-1 rounded-lg bg-rose-500/10 text-rose-400 font-bold border border-rose-500/20">
                                                {att.count.toLocaleString()} drops
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ═══ Right Column (4 cols): Precision Controls & Live Telemetry Stream ═══ */}
                    <div className="lg:col-span-4 space-y-8">
                        {/* Investigation Scope Filters Card */}
                        <div className="p-6 rounded-3xl bg-[#0B101D] border border-white/[0.08] shadow-xl space-y-4">
                            <div className="flex items-center justify-between pb-3 border-b border-white/[0.06]">
                                <h3 className="text-xs font-mono font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                    <SlidersHorizontal className="w-4 h-4 text-emerald-400" />
                                    Investigation Scope
                                </h3>
                                <button
                                    onClick={resetFilters}
                                    className="text-[10px] font-mono text-slate-400 hover:text-white uppercase tracking-wider transition-colors"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="space-y-3">
                                {/* Search Input */}
                                <div>
                                    <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block mb-1">
                                        Search IP / Policy / Service
                                    </label>
                                    <div className="relative">
                                        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
                                        <input
                                            type="text"
                                            value={filters.search}
                                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                            placeholder="e.g. 192.168.1.1, SSL, DNS..."
                                            className="w-full bg-[#0E1526] border border-white/[0.08] rounded-xl pl-9 pr-4 py-2.5 text-xs text-white font-mono placeholder:text-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* Sensor / Gateway Dropdown */}
                                <div>
                                    <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block mb-1">
                                        Sensor / Detection Engine
                                    </label>
                                    <select
                                        value={filters.device}
                                        onChange={(e) => setFilters(prev => ({ ...prev, device: e.target.value }))}
                                        className="w-full bg-[#0E1526] border border-white/[0.08] rounded-xl px-3 py-2.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-blue-500 transition-colors"
                                    >
                                        <option value="all">All Engines & Fleet</option>
                                        <option value="defender">🛡️ Microsoft Defender XDR</option>
                                        <option value="threatdown">⚡ ThreatDown (Malwarebytes) EDR</option>
                                        <option value="fortigate">🔥 All FortiGate Firewalls</option>
                                        {analysisData?.availableDevices?.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Severity Dropdown */}
                                <div>
                                    <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block mb-1">
                                        Severity Level
                                    </label>
                                    <select
                                        value={filters.severity}
                                        onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
                                        className="w-full bg-[#0E1526] border border-white/[0.08] rounded-xl px-3 py-2.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-blue-500 transition-colors"
                                    >
                                        <option value="all">All Severities</option>
                                        <option value="critical">Critical</option>
                                        <option value="alert">Alert</option>
                                        <option value="error">Error</option>
                                        <option value="warning">Warning</option>
                                        <option value="notice">Notice</option>
                                        <option value="information">Information</option>
                                    </select>
                                </div>

                                {/* Category & Action Row */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block mb-1">
                                            Category
                                        </label>
                                        <select
                                            value={filters.category}
                                            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                                            className="w-full bg-[#0E1526] border border-white/[0.08] rounded-xl px-2.5 py-2.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-blue-500 transition-colors"
                                        >
                                            <option value="all">All</option>
                                            <option value="SSL">SSL / TLS</option>
                                            <option value="VPN">VPN / Auth</option>
                                            <option value="General Traffic">General</option>
                                            <option value="IPS">IPS</option>
                                            <option value="Web Filter">Web Filter</option>
                                            <option value="DNS">DNS</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block mb-1">
                                            Action
                                        </label>
                                        <select
                                            value={filters.action}
                                            onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                                            className="w-full bg-[#0E1526] border border-white/[0.08] rounded-xl px-2.5 py-2.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-blue-500 transition-colors"
                                        >
                                            <option value="all">All</option>
                                            <option value="accept">Accept / Pass</option>
                                            <option value="deny">Deny / Block</option>
                                            <option value="close">Close</option>
                                            <option value="client-rst">Client Reset</option>
                                            <option value="server-rst">Server Reset</option>
                                        </select>
                                    </div>
                                </div>

                                <button
                                    onClick={applyFilters}
                                    disabled={loadingResults}
                                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-mono text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 disabled:opacity-50"
                                >
                                    {loadingResults ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Filter className="w-4 h-4" />
                                    )}
                                    {loadingResults ? "Filtering Telemetry..." : "Apply Tactical Filters"}
                                </button>
                            </div>
                        </div>

                        {/* Live Telemetry Stream Feed Card */}
                        <div className="rounded-3xl bg-[#0B101D] border border-white/[0.08] shadow-xl overflow-hidden flex flex-col h-[750px]">
                            <div className="p-5 border-b border-white/[0.06] bg-[#0E1526] flex justify-between items-center">
                                <div className="flex items-center gap-2.5">
                                    <Hash className="w-4 h-4 text-purple-400" />
                                    <h3 className="text-xs font-mono font-bold text-white uppercase tracking-wider">
                                        Telemetry Feed ({results.length})
                                    </h3>
                                </div>
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 font-bold bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                                    LIVE
                                </span>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                                {results.length > 0 ? (
                                    results.map((log) => (
                                        <div
                                            key={log.id || log._id}
                                            onClick={() => setSelectedLog(log)}
                                            className="p-4 rounded-2xl bg-[#0E1526] border border-white/[0.04] hover:border-blue-500/40 hover:bg-[#131C33] transition-all cursor-pointer group"
                                        >
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                                <span className="text-[10px] font-mono text-slate-400 font-semibold">
                                                    {log.timeStr} • {log.dateStr}
                                                </span>
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider border ${getSeverityBadgeClass(log.severity)}`}>
                                                        {log.severity}
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider border ${getActionBadgeClass(log.action)}`}>
                                                        {log.action}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* IP Flow */}
                                            <div className="text-xs font-mono font-bold text-slate-200 group-hover:text-blue-300 transition-colors flex items-center gap-1.5 truncate">
                                                <span>{log.srcip}</span>
                                                <span className="text-slate-500">→</span>
                                                <span>{log.dstip}</span>
                                            </div>

                                            {/* Summary message */}
                                            <p className="text-[11px] text-slate-400 leading-snug font-sans mt-1.5 line-clamp-2">
                                                {log.message || log.msg || `${log.action} on ${log.service}`}
                                            </p>

                                            <div className="mt-3 pt-2.5 border-t border-white/[0.04] flex items-center justify-between text-[10px] font-mono text-slate-400">
                                                <span className="flex items-center gap-1 truncate max-w-[150px]">
                                                    <Server className="w-3 h-3 text-slate-500" />
                                                    {log.devname}
                                                </span>
                                                <span className="text-cyan-400 font-bold">{log.service}</span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-8 text-center text-slate-500 font-mono text-xs">
                                        No telemetry records match current filters
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Forensic Log Details Modal / Drawer ─── */}
            <AnimatePresence>
                {selectedLog && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-[#0B101D] border border-white/[0.1] rounded-3xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="p-6 border-b border-white/[0.08] bg-[#0E1526] flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2.5 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider border ${getSeverityBadgeClass(selectedLog.severity)}`}>
                                            {selectedLog.severity}
                                        </span>
                                        <span className="text-xs font-mono text-slate-400">
                                            Gateway: <strong className="text-white">{selectedLog.devname}</strong>
                                        </span>
                                    </div>
                                    <h3 className="text-base font-black text-white mt-1 font-mono">
                                        Log Record: {String(selectedLog._id || selectedLog.id)}
                                    </h3>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={async () => {
                                            const ok = await copyToClipboard(JSON.stringify(selectedLog, null, 2));
                                            if (ok) {
                                                setCopiedLogId(true);
                                                setTimeout(() => setCopiedLogId(false), 2000);
                                                showToast("📋 Log JSON copied to clipboard!", "success");
                                            }
                                        }}
                                        className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-white/[0.08] text-xs font-mono font-bold flex items-center gap-1.5 transition-all"
                                    >
                                        {copiedLogId ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                                        {copiedLogId ? "Copied!" : "Copy JSON"}
                                    </button>
                                    <button
                                        onClick={() => setSelectedLog(null)}
                                        className="p-2 rounded-xl bg-slate-800 hover:bg-rose-500/20 hover:text-rose-400 text-slate-400 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar flex-1">
                                {/* Flow Card */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="p-4 rounded-2xl bg-[#0E1526] border border-white/[0.04]">
                                        <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block mb-1">
                                            Source Ingress
                                        </span>
                                        <div className="text-lg font-mono font-bold text-white">
                                            {selectedLog.srcip}
                                        </div>
                                        <div className="mt-2 text-xs font-mono text-slate-400 space-y-1">
                                            <div>Port: {selectedLog.srcport || "N/A"}</div>
                                            <div>Country: {selectedLog.srcCountry || "Reserved"}</div>
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-2xl bg-[#0E1526] border border-white/[0.04]">
                                        <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block mb-1">
                                            Destination Egress
                                        </span>
                                        <div className="text-lg font-mono font-bold text-white">
                                            {selectedLog.dstip}
                                        </div>
                                        <div className="mt-2 text-xs font-mono text-slate-400 space-y-1">
                                            <div>Port: {selectedLog.dstport || "N/A"}</div>
                                            <div>Service: <span className="text-cyan-400 font-bold">{selectedLog.service}</span></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Parsed Attributes Grid */}
                                <div>
                                    <h4 className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest mb-3">
                                        Parsed FortiOS Attributes
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                                        {Object.entries(selectedLog).map(([k, v]) => {
                                            if (typeof v === "object" || k === "_id") return null;
                                            return (
                                                <div key={k} className="p-2.5 rounded-xl bg-[#0E1526] border border-white/[0.03] flex justify-between items-center">
                                                    <span className="text-slate-500 font-bold">{k}</span>
                                                    <span className="text-slate-200 truncate max-w-[200px] text-right font-normal">
                                                        {String(v)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer Actions */}
                            <div className="p-5 border-t border-white/[0.08] bg-[#0E1526] flex flex-wrap items-center justify-end gap-3">
                                {selectedLog.srcip && selectedLog.srcip !== "LAN Host" && (
                                    <button
                                        onClick={(e) => {
                                            if (stagingStatus[selectedLog._id] === "staged") {
                                                navigate("/firewall-approvals");
                                            } else {
                                                handleStageBlockRule(e, selectedLog);
                                            }
                                        }}
                                        disabled={stagingStatus[selectedLog._id] === "staging"}
                                        className={`px-5 py-2.5 rounded-xl font-mono text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all shadow-lg ${stagingStatus[selectedLog._id] === "staged"
                                            ? "bg-emerald-600 hover:bg-emerald-500 text-white ring-2 ring-emerald-400/50"
                                            : "bg-rose-600 hover:bg-rose-500 text-white"
                                            }`}
                                    >
                                        {stagingStatus[selectedLog._id] === "staged" ? (
                                            <>
                                                <ExternalLink className="w-4 h-4 text-emerald-200" />
                                                View in Approvals Queue →
                                            </>
                                        ) : (
                                            <>
                                                <ShieldAlert className="w-4 h-4" />
                                                Stage Perimeter Block
                                            </>
                                        )}
                                    </button>
                                )}

                                <button
                                    onClick={(e) => handleCreateZoho(e, selectedLog)}
                                    disabled={zohoStatus[selectedLog._id] === "loading" || zohoStatus[selectedLog._id] === "created"}
                                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all shadow-lg disabled:opacity-50"
                                >
                                    {zohoStatus[selectedLog._id] === "created" ? (
                                        <>
                                            <CheckCircle2 className="w-4 h-4" /> Ticket Created
                                        </>
                                    ) : (
                                        <>
                                            <Ticket className="w-4 h-4" /> Create Zoho Ticket
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(59, 130, 246, 0.25); border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
            `}</style>
        </div>
    );
}
